import { useEffect, useRef, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";
import { Check, CheckCheck, CircleAlert, CircleCheck, SendHorizontal, Smile } from "lucide-react";

/**
 * De WhatsApp-tegel bij een relatie.
 *
 * <p>Je kunt niet van tevoren weten of een nummer WhatsApp heeft -- Meta vertelt dat niet, en de
 * oude manier om het te vragen bestaat niet meer. Daarom is deze tegel optimistisch: hij laat het
 * nummer zien en je probeert het gewoon. Weigert Meta omdat er geen WhatsApp op dat nummer zit, dan
 * staat dát er, in de woorden van Meta zelf. Een knop verbergen omdat we het niet zeker weten is
 * slechter: dan kun je niets, en weet je nog steeds niet waarom.
 *
 * <p>Het nummer komt van de server uit het dossier en wordt hier niet ingetypt. Dat scheelt niet
 * alleen een vergissing, het voorkomt ook dat iemand namens het asiel naar een willekeurig nummer
 * appt.
 */
interface Regel {
  richting: "IN" | "UIT";
  soort: string;
  tekst: string | null;
  naam: string | null;
  tijdstip: string;
  /** Alleen bij uitgaand: sent, delivered, read of failed, zoals Meta het meldt. */
  status?: string | null;
}

interface Stand {
  beschikbaar: boolean;
  nummer: string | null;
  gesprek?: Regel[];
}

interface Antwoord {
  gelukt: boolean;
  berichtId: string | null;
  melding: string | null;
}

/** Wat je in een bericht aan een klant het vaakst nodig hebt; geen volledige emojikiezer. */
const EMOJI = ["😊", "🙂", "😀", "😂", "😍", "😎", "👍", "👏", "🙏", "❤️", "🐶", "🐱", "🐾", "🎉", "✅", "📅", "📞", "🏠", "☀️", "😢"];

/** Dag en tijd zoals je ze in een gesprek leest; vandaag alleen de tijd. */
function wanneer(iso: string): string {
  const d = new Date(iso);
  const vandaag = new Date().toDateString() === d.toDateString();
  const tijd = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  return vandaag ? tijd : `${d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} ${tijd}`;
}

/** De vinkjes onder een uitgaand bericht: één grijs verstuurd, twee grijs afgeleverd, twee groen gelezen. */
function Vinkjes({ status, t }: { status?: string | null; t: (k: string) => string }) {
  if (status === "failed") {
    return <CircleAlert size={14} className="fawkes-wa__status fawkes-wa__status--mislukt" aria-label={t("whatsapp.status.failed")} />;
  }
  if (status === "read") {
    return <CheckCheck size={15} className="fawkes-wa__status fawkes-wa__status--gelezen" aria-label={t("whatsapp.status.read")} />;
  }
  if (status === "delivered") {
    return <CheckCheck size={15} className="fawkes-wa__status" aria-label={t("whatsapp.status.delivered")} />;
  }
  return <Check size={15} className="fawkes-wa__status" aria-label={t("whatsapp.status.sent")} />;
}

/**
 * Het WhatsApp-beeldmerk in een rondje, als icoon voor de tegelkop. Zelfde vorm als de lucide-iconen
 * van de andere tegels (een {@code size}-prop), zodat {@code tegelTitel} hem net zo neerzet.
 */
export function WhatsAppLogo({ size = 18 }: { size?: number | string }) {
  return (
    <span className="fawkes-wa-logo" aria-hidden="true">
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" focusable="false">
        <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.8h-.01a9.8 9.8 0 0 1-5-1.37l-.36-.21-3.72.97.99-3.62-.23-.37a9.8 9.8 0 0 1-1.5-5.22c0-5.42 4.41-9.83 9.84-9.83a9.77 9.77 0 0 1 6.95 2.88 9.77 9.77 0 0 1 2.88 6.96c0 5.42-4.41 9.83-9.83 9.83m8.37-18.2A11.75 11.75 0 0 0 12.05.13C5.5.13.17 5.46.17 12.01c0 2.1.55 4.14 1.59 5.94L.07 24l6.2-1.63a11.84 11.84 0 0 0 5.77 1.47h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.24-6.16-3.48-8.4" />
      </svg>
    </span>
  );
}

export function WhatsAppTile({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const [tekst, setTekst] = useState("");
  const [uitkomst, setUitkomst] = useState<Antwoord | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const veld = useRef<HTMLTextAreaElement>(null);
  const gesprekRef = useRef<HTMLOListElement>(null);

  // Elke tien seconden opnieuw: zo vaak haalt de server ook binnengekomen berichten op bij het platform.
  const { data, isLoading, refetch } = useCustom<Stand>({
    url: `relaties/${relatieId}/whatsapp`,
    method: "get",
    queryOptions: { refetchInterval: 10_000 },
  });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const stand = data?.data;
  const aantal = stand?.gesprek?.length ?? 0;

  // Een nieuw bericht onderaan in beeld, zoals in elke chat; scrollt niet als je zelf terugleest en
  // er niets bijkomt.
  useEffect(() => {
    const lijst = gesprekRef.current;
    if (lijst) lijst.scrollTop = lijst.scrollHeight;
  }, [aantal]);

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  // Geen nummer voor de vestiging waarin je werkt, en ook geen nummer van de server om op terug te vallen.
  if (!stand?.beschikbaar) {
    return <p className="fawkes-muted">{t("whatsapp.nietGekoppeld")}</p>;
  }
  if (!stand.nummer) {
    return <p className="fawkes-muted">{t("whatsapp.geenNummer")}</p>;
  }

  const stuur = () => {
    setUitkomst(null);
    setEmojiOpen(false);
    mutate(
      {
        url: `relaties/${relatieId}/whatsapp`,
        method: "post",
        values: { tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const uit = res?.data as unknown as Antwoord;
          setUitkomst(uit);
          if (uit?.gelukt) {
            setTekst("");
            refetch();
          }
        },
        onError: () => setUitkomst({ gelukt: false, berichtId: null, melding: t("whatsapp.mislukt") }),
      },
    );
  };

  /** Een emoji op de plek van de cursor, niet achteraan. */
  const voegToe = (emoji: string) => {
    const el = veld.current;
    const van = el?.selectionStart ?? tekst.length;
    const tot = el?.selectionEnd ?? tekst.length;
    setTekst(tekst.slice(0, van) + emoji + tekst.slice(tot));
    setUitkomst(null);
    // Dicht na een keuze: open bedekt hij het gesprek, en meestal wil je er één.
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(van + emoji.length, van + emoji.length);
    });
  };

  return (
    <div className="fawkes-wa">
      <p className="fawkes-wa__naar">
        {t("whatsapp.naar")} <b>{stand.nummer}</b>
      </p>
      <div className="fawkes-wa__vlak">
        {aantal > 0 ? (
          <ol className="fawkes-wa__gesprek" aria-label={t("whatsapp.gesprek")} ref={gesprekRef}>
            {stand.gesprek!.map((r, i) => (
              <li key={i} className={r.richting === "IN" ? "fawkes-wa__in" : "fawkes-wa__uit"}>
                <span className="fawkes-wa__bubbel">
                  {r.tekst ?? <i>{t("whatsapp.zonderTekst", { args: [r.soort] })}</i>}
                </span>
                <span className="fawkes-wa__wanneer">
                  {r.richting === "IN" && r.naam ? `${r.naam} · ` : ""}
                  {wanneer(r.tijdstip)}
                  {r.richting === "UIT" && <Vinkjes status={r.status} t={t} />}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="fawkes-wa__leeg">{t("whatsapp.leegGesprek")}</p>
        )}
      </div>
      <div className="fawkes-wa__invoer">
        <textarea
          ref={veld}
          className="fawkes-wa__tekst"
          rows={3}
          maxLength={4096}
          value={tekst}
          placeholder={t("whatsapp.plaatshouder")}
          aria-label={t("whatsapp.tekstLabel")}
          onChange={(e) => { setTekst(e.target.value); setUitkomst(null); }}
        />
        <button
          type="button"
          className="fawkes-wa__emojiknop"
          aria-label={t("whatsapp.emoji")}
          aria-expanded={emojiOpen}
          title={t("whatsapp.emoji")}
          onClick={() => setEmojiOpen((o) => !o)}
        >
          <Smile size={20} aria-hidden="true" />
        </button>
        {emojiOpen && (
          <div className="fawkes-wa__emojis" role="listbox" aria-label={t("whatsapp.emoji")}>
            {EMOJI.map((e) => (
              <button key={e} type="button" role="option" aria-selected={false} onClick={() => voegToe(e)}>
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="fawkes-wa__balk">
        <span className="fawkes-wa__uitkomst" role="status">
          {uitkomst && (uitkomst.gelukt ? (
            <span className="fawkes-wa__ok"><CircleCheck size={18} aria-hidden="true" /> {t("whatsapp.verstuurd")}</span>
          ) : (
            <span className="fawkes-wa__fout"><CircleAlert size={18} aria-hidden="true" /> {uitkomst.melding || t("whatsapp.mislukt")}</span>
          ))}
        </span>
        <Button variant="primary" disabled={bezig || !tekst.trim()} onClick={stuur}>
          <SendHorizontal size={16} aria-hidden="true" /> {t(bezig ? "whatsapp.bezig" : "whatsapp.versturen")}
        </Button>
      </div>
    </div>
  );
}
